import { ChevronDown } from 'lucide-react';
import * as React from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAnchoredPopover } from '@/components/ui/use-anchored-popover';
import { matchesSearch } from '@/lib/search-fold';

// =============================================================================
// filter-combo.tsx — Liste déroulante AUTOFILTRANTE à sélection multiple (filtre « Google Sheets »).
// =============================================================================
// Demande GATE_HUMAN du 2026-09-21 (verbatim) : « dans les champs de recherche (tous) je voudrais
// implémenter un filtre en style google sheet avec une liste déroulante autofiltrante. […] Quand
// l'utilisateur rentre des lettres, la liste déroulante affiche les 5 premiers fournisseurs qui
// répondent aux critères digités. » Arbitrages du même jour : sélection MULTIPLE par cases à cocher ;
// au-delà de 5 correspondances la liste DÉFILE et montre tout (pas de « +N autres »).
//
// -----------------------------------------------------------------------------------------------
// POURQUOI L'OPTION (b) — socle partagé — PLUTÔT QUE (a) — composition sur `FilterPopover`.
// -----------------------------------------------------------------------------------------------
// `FilterPopover` (routes/admin/operational-view/operational-view-filter-menu.tsx) portait déjà le
// travail difficile : portail, ancrage recalculé (`useAnchorReflow`), bascule verticale, clic
// extérieur, restitution du focus. Ce travail n'est PAS réécrit ici : il a été extrait tel quel dans
// `use-anchored-popover.ts`, dont les DEUX composants dépendent désormais (API et comportement de
// `FilterPopover` strictement inchangés, ses usages et ses tests aussi).
//
// L'option (a), passer le champ de filtre et la liste en `children` de `FilterPopover`, a été écartée
// après lecture du code, pour trois raisons mesurables :
//   1. CLAVIER. Le handler de `FilterPopover` ne traite les flèches que si la cible est une case
//      (`target.type !== 'checkbox'` ⇒ break) : « ↓ depuis le champ de filtre entre dans la liste »,
//      exigé par la spec, y serait inatteignable sans intercepter l'événement dans les `children` et
//      l'arrêter avant le parent. Deux handlers concurrents sur le même panneau, avec des
//      `stopPropagation` ciblés, est un piège de maintenance pour un gain nul.
//   2. DÉFILEMENT. Le panneau de `FilterPopover` est lui-même `max-h-80 overflow-y-auto`. La liste
//      défilante exigée par l'arbitrage 2 donnerait DEUX conteneurs défilants imbriqués, l'en-tête
//      (champ de filtre, compteur, actions) glissant hors de vue pendant qu'on fait défiler la liste.
//      Ici l'en-tête est FIXE et seule la liste défile.
//   3. FOCUS. `FilterPopover` focalise le premier `input` du panneau à l'ouverture ; ce serait bien le
//      champ de filtre, mais par coïncidence de l'ordre du DOM, pas par contrat. Le socle expose un
//      crochet `onOpened` explicite.
//
// -----------------------------------------------------------------------------------------------
// ACCESSIBILITÉ (WCAG 2.1 AA, ST-NFR-03)
// -----------------------------------------------------------------------------------------------
// Déclencheur <button aria-expanded aria-controls> (pattern disclosure, comme `FilterPopover`).
// Panneau role="group" nommé, contenant un champ `type="search"` nommé par un <label> lié, un
// compteur en role="status" (le nombre de correspondances est ANNONCÉ, pas seulement affiché), deux
// actions, puis les cases à cocher, chacune nommée par son <label htmlFor>. Le choix d'un groupe de
// cases plutôt que d'un `listbox aria-multiselectable` est délibéré : même arbitrage que
// `FilterPopover`, le motif natif est plus robuste avec les lecteurs d'écran.
// Clavier : ↓ ouvre depuis le déclencheur ; à l'ouverture le focus va au CHAMP ; ↓ depuis le champ
// entre dans la liste (↑ y entre par la fin) ; ↑↓ y circulent en boucle ; Entrée bascule l'option ;
// Échap ferme et rend le focus au déclencheur ; Tab sortant du panneau le ferme de même.
// Aucun état n'est porté par la seule couleur : une option sélectionnée l'est par sa case cochée, et
// le nombre de sélections est porté par le libellé du déclencheur, que l'appelant compose.

/** Option proposée par la liste. `hint` est un complément (SIRET, code) AUSSI pris en compte par la recherche. */
export interface FilterComboOption {
  readonly value: string;
  readonly label: string;
  readonly hint?: string;
}

export interface FilterComboProps {
  /** Libellé visible du déclencheur, ex. « Fournisseur · 3 sélectionnés ». Composé par l'appelant. */
  readonly triggerLabel: string;
  /** Nom accessible du panneau, ex. « Fournisseur ». */
  readonly groupLabel: string;
  /** Options proposées. Le composant les trie lui-même (voir `sortOptions`). */
  readonly options: readonly FilterComboOption[];
  /** Valeurs cochées. Liste VIDE = aucun filtre, donc « tous » : jamais de valeur sentinelle. */
  readonly selected: readonly string[];
  /** Notifié à chaque changement. La liste rendue suit l'ordre alphabétique des options. */
  readonly onChange: (next: readonly string[]) => void;
  /**
   * Notifié à chaque frappe dans le champ de filtre, ET à l'ouverture (avec la chaîne vide, puisque
   * chaque ouverture repart d'une recherche vierge).
   *
   * POURQUOI. Le filtrage du composant reste LOCAL en toutes circonstances ; ce crochet permet en plus
   * à l'appelant d'alimenter `options` depuis le SERVEUR (cas du panneau Fournisseurs du détail
   * campagne, dont le référentiel n'est pas chargé côté client). Les deux filtrages se COMPOSENT sans
   * se contredire : le serveur élargit la matière, le filtre local resserre à la frappe et rend donc
   * un retour immédiat pendant le temps d'aller-retour réseau. L'appelant reste libre de l'ignorer,
   * auquel cas le composant se comporte exactement comme avant (câblage du lot F1 inchangé).
   */
  readonly onQueryChange?: ((query: string) => void) | undefined;
  readonly searchPlaceholder?: string;
  /** Alignement du panneau sous le déclencheur (hérité du socle). */
  readonly align?: 'start' | 'end';
}

/**
 * Tri alphabétique français, stable, insensible à la casse et aux accents pour l'ORDRE (`localeCompare`
 * avec `sensitivity: 'base'` place « Élite » entre « Delta » et « Fidia », pas en fin de liste).
 *
 * La spec confie le tri à l'appelant ; le composant le refait quand même. Ce durcissement coûte un
 * `toSorted` par rendu de panneau et supprime une classe entière de régressions : avec une quinzaine
 * de points d'appel prévus, l'invariant « la liste est alphabétique » ne peut pas dépendre de la
 * vigilance de chacun d'eux.
 */
function sortOptions(options: readonly FilterComboOption[]): readonly FilterComboOption[] {
  return [...options].sort((a, b) =>
    a.label.localeCompare(b.label, 'fr', { sensitivity: 'base', numeric: true }),
  );
}

/** Texte recherchable d'une option : son libellé et son complément. */
function optionMatches(option: FilterComboOption, query: string): boolean {
  return matchesSearch(`${option.label} ${option.hint ?? ''}`, query);
}

export function FilterCombo({
  triggerLabel,
  groupLabel,
  options,
  selected,
  onChange,
  onQueryChange,
  searchPlaceholder = 'Rechercher…',
  align = 'start',
}: FilterComboProps): React.JSX.Element {
  const [query, setQuery] = React.useState('');
  // Options cochées que le texte saisi EXCLUT. Elles restent affichées, en tête : on ne cache jamais à
  // l'utilisateur ce qu'il a coché. Cette liste est un INSTANTANÉ recalculé à chaque frappe, et NON une
  // valeur dérivée du rendu : sans cela, décocher une de ces options la ferait disparaître sous le
  // curseur, et le focus partirait au <body> en plein parcours clavier.
  const [pinnedValues, setPinnedValues] = React.useState<readonly string[]>([]);

  const baseId = React.useId();
  const searchId = `${baseId}-recherche`;

  const { open, popupId, triggerRef, popupRef, popupStyle, close, openPopover } =
    useAnchoredPopover({
      align,
      onOpened: (popup) => {
        // Le focus va au CHAMP DE FILTRE : l'utilisateur ouvre la liste pour taper, pas pour tabuler.
        popup.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
      },
    });

  const sorted = React.useMemo(() => sortOptions(options), [options]);
  // Identifiants DOM dérivés du RANG, jamais de la valeur : une valeur métier (raison sociale, libellé
  // de profil) contient des espaces, ce qu'un `id` HTML ne tolère pas pour un `<label htmlFor>`.
  const optionIds = React.useMemo(
    () => new Map(sorted.map((option, index) => [option.value, `${baseId}-${index}`])),
    [sorted, baseId],
  );
  const selectedSet = React.useMemo(() => new Set(selected), [selected]);
  const pinnedSet = React.useMemo(() => new Set(pinnedValues), [pinnedValues]);

  // Les correspondances du texte saisi, hors options épinglées (déjà affichées en tête, jamais deux fois).
  const matched = React.useMemo(
    () => sorted.filter((option) => !pinnedSet.has(option.value) && optionMatches(option, query)),
    [sorted, pinnedSet, query],
  );
  const pinned = React.useMemo(
    () => sorted.filter((option) => pinnedSet.has(option.value)),
    [sorted, pinnedSet],
  );

  /** Remet les valeurs dans l'ordre des options ; celles qu'on ne connaît pas (URL d'un référentiel plus
   * ancien) sont CONSERVÉES et renvoyées en fin de liste — un filtre non affichable n'est pas un filtre
   * à perdre silencieusement. */
  const orderValues = React.useCallback(
    (values: readonly string[]): readonly string[] => {
      const rank = new Map(sorted.map((option, index) => [option.value, index]));
      return [...values].sort(
        (a, b) =>
          (rank.get(a) ?? Number.MAX_SAFE_INTEGER) - (rank.get(b) ?? Number.MAX_SAFE_INTEGER),
      );
    },
    [sorted],
  );

  const toggleValue = (value: string): void => {
    const next = selectedSet.has(value)
      ? selected.filter((current) => current !== value)
      : orderValues([...selected, value]);
    onChange(next);
  };

  const applyQuery = (next: string): void => {
    setQuery(next);
    onQueryChange?.(next);
    // Recalcul de l'épinglage À LA FRAPPE (et seulement là), à partir de la sélection du moment.
    setPinnedValues(
      selected.filter((value) => {
        const option = sorted.find((candidate) => candidate.value === value);
        return option !== undefined && !optionMatches(option, next);
      }),
    );
  };

  const openFresh = (): void => {
    // Chaque ouverture repart d'une recherche vierge : la liste montre TOUTES les options, triées.
    // L'appelant qui alimente les options depuis le serveur est prévenu du même coup, sans quoi il
    // resterait sur les résultats de la recherche précédente alors que le champ, lui, est vide.
    setQuery('');
    setPinnedValues([]);
    onQueryChange?.('');
    openPopover();
  };

  const onTriggerClick = (): void => {
    if (open) {
      close(false);
      return;
    }
    openFresh();
  };

  /** Cases à cocher du panneau, dans l'ordre du DOM (navigation par flèches). */
  const optionInputs = React.useCallback(
    (): HTMLInputElement[] =>
      Array.from(
        popupRef.current?.querySelectorAll<HTMLInputElement>(
          'input[type="checkbox"]:not([disabled])',
        ) ?? [],
      ),
    [popupRef],
  );

  /** Tous les arrêts de tabulation du panneau : champ, actions, puis cases. Sert à détecter la SORTIE. */
  const tabbables = React.useCallback(
    (): HTMLElement[] =>
      Array.from(
        popupRef.current?.querySelectorAll<HTMLElement>(
          'input:not([disabled]), button:not([disabled])',
        ) ?? [],
      ),
    [popupRef],
  );

  const onPanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const target = event.target as HTMLElement;
    const inSearch = target instanceof HTMLInputElement && target.type === 'search';
    switch (event.key) {
      case 'Escape':
        // `stopPropagation` : une modale ou un tiroir englobant ne doit pas se fermer AUSSI.
        event.preventDefault();
        event.stopPropagation();
        close(true);
        break;
      case 'ArrowDown':
      case 'ArrowUp': {
        const items = optionInputs();
        if (items.length === 0) {
          break;
        }
        event.preventDefault();
        if (inSearch) {
          // Réflexe combobox : ↓ entre dans la liste par le haut, ↑ par le bas.
          (event.key === 'ArrowDown' ? items[0] : items[items.length - 1])?.focus();
          break;
        }
        const index = items.indexOf(document.activeElement as HTMLInputElement);
        if (index < 0) {
          items[0]?.focus();
          break;
        }
        const step = event.key === 'ArrowDown' ? 1 : -1;
        items[(index + step + items.length) % items.length]?.focus();
        break;
      }
      case 'Enter': {
        if (inSearch) {
          // Le panneau est portalé hors de tout <form> : rien à soumettre, et surtout rien à fermer.
          event.preventDefault();
          break;
        }
        if (target instanceof HTMLInputElement && target.type === 'checkbox') {
          // Une case ne bascule nativement qu'à la barre d'espace. La spec exige aussi Entrée.
          event.preventDefault();
          toggleValue(target.value);
        }
        break;
      }
      case 'Tab': {
        // Le panneau est portalé en fin de <body> : sortir par Tab rend le focus au déclencheur, ce qui
        // préserve l'ordre de tabulation perçu de la page.
        const stops = tabbables();
        const index = stops.indexOf(document.activeElement as HTMLElement);
        const leaving = event.shiftKey ? index <= 0 : index === stops.length - 1;
        if (leaving) {
          event.preventDefault();
          close(true);
        }
        break;
      }
      default:
        break;
    }
  };

  const selectAllMatched = (): void => {
    // Borné aux options FILTRÉES (arbitrage de la spec), en UNION avec la sélection déjà posée : une
    // recherche ne doit jamais décocher en silence ce qu'elle ne montre pas.
    const next = new Set(selected);
    for (const option of matched) {
      next.add(option.value);
    }
    onChange(orderValues([...next]));
  };

  // Les deux actions restent ACTIVES même sans effet possible : un bouton qui se désactive sous le
  // focus le renvoie au <body> en plein parcours clavier. Leur libellé porte déjà l'information.
  const counterText =
    matched.length === 0
      ? 'Aucune correspondance'
      : `${matched.length} correspondance${matched.length > 1 ? 's' : ''}`;

  return (
    <>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={open}
        aria-controls={open ? popupId : undefined}
        onClick={onTriggerClick}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' && !open) {
            event.preventDefault();
            openFresh();
          }
        }}
        title={triggerLabel}
        className="min-w-0 max-w-full gap-1 font-normal"
      >
        {/* Libellé TRONQUÉ visuellement ; le nom accessible reste la chaîne entière, rappelée en `title`. */}
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <ChevronDown aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
      </Button>
      {open && popupStyle !== null
        ? createPortal(
            // Le panneau n'est pas rendu interactif par ce `onKeyDown` : il DÉLÈGUE, par remontée
            // d'événement, les touches de ses descendants DÉJÀ focalisables (champ de recherche,
            // boutons, cases). Un seul handler y voit le parcours entier, ce qui est la condition
            // pour que ↓ passe du champ à la liste. La règle ne sait pas distinguer la délégation
            // d'un listener posé sur un élément inerte, d'où la levée LOCALE et argumentée.
            // (`FilterPopover` fait exactement la même chose sans être signalé, son `role` étant une
            // expression que la règle ne sait pas résoudre : une exemption de fait, pas de droit.)
            // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
            <div
              ref={popupRef}
              id={popupId}
              role="group"
              aria-label={groupLabel}
              onKeyDown={onPanelKeyDown}
              style={popupStyle}
              className="z-50 flex w-72 max-w-full flex-col rounded-md border border-border bg-card p-2 text-card-foreground shadow-md"
            >
              <Label htmlFor={searchId} className="sr-only">
                {`Rechercher dans ${groupLabel}`}
              </Label>
              <Input
                id={searchId}
                type="search"
                value={query}
                onChange={(event) => applyQuery(event.target.value)}
                placeholder={searchPlaceholder}
                autoComplete="off"
                className="h-9"
              />
              <div className="flex items-center justify-between gap-2 pt-2">
                <div className="flex items-center gap-1">
                  <Button type="button" variant="ghost" size="sm" onClick={selectAllMatched}>
                    {`Tout sélectionner (${matched.length})`}
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={() => onChange([])}>
                    Effacer
                  </Button>
                </div>
                {/* Compteur ANNONCÉ (role="status") : l'effet d'une frappe ne se lit pas qu'à l'écran. */}
                <p role="status" className="shrink-0 text-xs text-muted-foreground">
                  {counterText}
                </p>
              </div>
              {/* Seule la LISTE défile (arbitrage 2 : liste complète, pas de « +N autres ») ; l'en-tête
                  reste visible. ~10 lignes visibles à la fois. */}
              <div className="mt-1 max-h-80 overflow-y-auto">
                {pinned.length > 0 ? (
                  <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    Déjà sélectionnés, hors recherche
                  </p>
                ) : null}
                {pinned.map((option) => (
                  <OptionRow
                    key={option.value}
                    id={optionIds.get(option.value) ?? option.value}
                    option={option}
                    checked={selectedSet.has(option.value)}
                    onToggle={toggleValue}
                  />
                ))}
                {pinned.length > 0 && matched.length > 0 ? (
                  <p className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    Correspondances
                  </p>
                ) : null}
                {matched.map((option) => (
                  <OptionRow
                    key={option.value}
                    id={optionIds.get(option.value) ?? option.value}
                    option={option}
                    checked={selectedSet.has(option.value)}
                    onToggle={toggleValue}
                  />
                ))}
                {matched.length === 0 && pinned.length === 0 ? (
                  // Jamais une liste vide muette : on dit POURQUOI il n'y a rien.
                  <p className="px-2 py-3 text-sm text-muted-foreground">
                    Aucune option ne correspond à cette recherche.
                  </p>
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

interface OptionRowProps {
  readonly id: string;
  readonly option: FilterComboOption;
  readonly checked: boolean;
  readonly onToggle: (value: string) => void;
}

/**
 * Une ligne d'option : case + <label htmlFor> cliquable. Le motif est celui de `CheckboxOption`
 * (operational-view-filter-menu), volontairement REDÉCLARÉ ici : `components/ui` ne doit pas dépendre
 * de `routes/` (sens d'importation), et la ligne porte en plus un complément (`hint`) et l'attribut
 * `value` que le clavier du panneau lit pour basculer l'option à la touche Entrée.
 */
function OptionRow({ id, option, checked, onToggle }: OptionRowProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground">
      <input
        id={id}
        type="checkbox"
        value={option.value}
        className="size-4 shrink-0 rounded border-input"
        checked={checked}
        onChange={() => onToggle(option.value)}
      />
      <Label htmlFor={id} className="min-w-0 flex-1 cursor-pointer font-normal">
        <span className="truncate">{option.label}</span>
        {option.hint === undefined ? null : (
          <>
            {/* Espace TEXTUEL, en plus de la gouttière `ml-2`, et il n'est pas décoratif. Le nom
                accessible d'une case est la concaténation du texte de son <label> ; Chromium
                n'insère AUCUN séparateur entre deux <span> inline adjacents (contrairement à
                dom-accessibility-api utilisé par testing-library, d'où un vert jsdom trompeur).
                Sans ce nœud, « ACME SAS » + « 55210055400013 » donne le nom
                « ACME SAS55210055400013 » : un lecteur d'écran énonce un seul mot collé, et
                WCAG 2.5.3 (Label in Name) n'est plus tenu puisque le nom accessible ne contient
                plus le libellé visible tel qu'il est lu. Mesuré en Chromium réel le 2026-09-21
                (ST-CAMPAIGN-01 T1, voir le rapport du lot F1-bis). */}
            {' '}
            <span className="ml-2 text-xs text-muted-foreground">{option.hint}</span>
          </>
        )}
      </Label>
    </div>
  );
}
